import { PlusIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@follow/ui';
import ModalEquipment from '../../../../components/Modals/ModalEquipment';
import AssetsService from '../../../../services/AssetsService';
import { Equipment } from '../../../../services/models/assets';
import { useQuery, useQueryClient } from '@tanstack/react-query';

const DashboardEquipments = () => {
  const { stationId } = useParams();
  const numericStationId = stationId != null ? Number(stationId) : undefined;
  const [selectedEquipment, setSelectedEquipment] = useState<Partial<Equipment>>();

  const { data: equipments } = useQuery({
    queryKey: ['equipments', numericStationId],
    queryFn: () => AssetsService.getStationEquipments(numericStationId!),
    select: (data) => data.equipments,
    enabled: numericStationId != null,
  });

  const queryClient = useQueryClient();

  const closeAndRefresh = () => {
    setSelectedEquipment(undefined);
    queryClient.invalidateQueries({ queryKey: ['equipments'] });
  };

  return (
    <div className="w-full max-w-[1280px] px-4 flex flex-col gap-4 pb-8">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-neutral typo-h1">Équipements</h1>
        <Button
          variant="primary"
          size="sm"
          tone="light"
          icon={<PlusIcon className="w-4 h-4" />}
          onClick={() => setSelectedEquipment({})}
        >
          Ajouter un équipement
        </Button>
      </div>

      <div className="bg-white rounded-xl shadow-card outline outline-1 outline-offset-[-1px] outline-neutral-10 overflow-hidden">
        <div className="overflow-x-auto">
          <div className="min-w-[640px]">
            {/* Column headers */}
            <div className="flex items-center px-5 py-2 border-b border-neutral-10">
              <span className="w-14 flex-shrink-0" />
              <span className="flex-grow typo-small tracking-[0.03em] !font-bold uppercase text-neutral-40">Modèle</span>
              <span className="w-40 flex-shrink-0 typo-small tracking-[0.03em] !font-bold uppercase text-neutral-40">Station</span>
              <span className="w-24 flex-shrink-0 typo-small tracking-[0.03em] !font-bold uppercase text-neutral-40">Lock</span>
            </div>

            {/* Rows */}
            {(equipments?.length ?? 0) === 0 ? (
              <div className="py-8 text-center typo-small text-neutral-40">
                Aucun équipement
              </div>
            ) : (
              equipments?.map((equipment) => (
                <button
                  key={equipment.id}
                  type="button"
                  onClick={() => setSelectedEquipment(equipment)}
                  className="flex w-full items-center px-5 py-2.5 border-b border-neutral-5 hover:bg-neutral-5 transition-colors text-left last:border-b-0"
                >
                  <div className="w-14 flex-shrink-0">
                    <img
                      src={equipment.imageUrl}
                      alt={equipment.model}
                      className="w-10 h-10 rounded-lg object-cover bg-neutral-5"
                    />
                  </div>
                  <div className="flex-grow min-w-0 pr-2">
                    <p className="typo-body text-neutral !font-semibold line-clamp-1">
                      {equipment.model}
                    </p>
                    <p className="typo-small text-neutral-60 line-clamp-1">
                      {equipment.description}
                    </p>
                  </div>
                  <span className="w-40 flex-shrink-0 typo-small text-neutral-60 truncate pr-2">
                    {equipment.lock?.ecu?.station?.name ?? '—'}
                  </span>
                  <span className="w-24 flex-shrink-0 typo-small text-neutral !font-bold">
                    {equipment.lock ? `Lock ${equipment.lock.position + 1}` : '—'}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      </div>

      <ModalEquipment
        equipment={selectedEquipment}
        onClose={closeAndRefresh}
        visible={!!selectedEquipment}
      />
    </div>
  );
};

export default DashboardEquipments;
